import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Stack } from 'react-bootstrap'
import { useFormikContext } from 'formik'
import type { FormikProps } from 'formik'
import type { Chat } from '@ztechapps/common.contract'
import { useMergeChat } from '@ztechapps/chat.common'
import { useToast } from '@ztechapps/common.hook'
import type { DataLoadOptions, SelectOption } from '@ztechapps/webui.input'
import type { ModalTriggerProps } from '@ztechapps/webui.modal'
import { BusyButton, SaveCancelButtons } from '@ztechapps/webui.button'
import { Field, FormModal } from '@ztechapps/webui.form'
import { Alert, Icon, ShowIf } from '@ztechapps/webui.util'
import { ConversationSelect } from '../ConversationSelect'
import { buildConversationSearch } from '../ConversationSelect/buildConversationSearch'
import { conversationSelectOptions } from '../ConversationSelect/conversationSelectOptions'
import { useConversationInboxContext } from './Context'
import { mergeableChatsQuery } from './graphql'

type MergeFormValues = { target?: SelectOption }

type MergeConversationProps = {
  /**
   * The currently open conversation. It becomes the merge SOURCE — it keeps its messages, the picked
   * target receives a note with its last message, and it is retired as a tombstone.
   */
  sourceChat?: Chat
}

const MergeTrigger = ({ onClick }: ModalTriggerProps) => (
  <BusyButton icon="code-merge" text="Merge into..." onClick={onClick} />
)

const targetRequired = 'Pick the conversation to merge into.'

const validate = ({ target }: MergeFormValues) => (target?._id ? {} : { target: targetRequired })

// The server's refusal belongs to the target it was raised for, so picking another target lifts it.
type Refusal = { targetId?: string; message: string }

const refusalFor = (refusal?: Refusal, target?: SelectOption) =>
  refusal && refusal.targetId === target?._id ? refusal.message : undefined

const MergeRefusal = ({ refusal }: { refusal?: Refusal }) => {
  const { values } = useFormikContext<MergeFormValues>()
  const message = refusalFor(refusal, values.target)
  return (
    <ShowIf condition={!!message}>
      <Alert type="danger" className="mb-0">
        <Icon name="exclamation-triangle" className="me-2" aria-hidden="true" />
        {message}
      </Alert>
    </ShowIf>
  )
}

const noOptionsMessage = ({ inputValue }: { inputValue: string }) =>
  inputValue ? 'Cannot find any matching conversations.' : 'No other conversation can be merged into this one.'

// The help says why Merge is off until a target is picked. `InlineDisplayContent` draws it twice: as the label's "?"
// hover tooltip, and (`showHelpAsInline`) as text under the picker, which stays on screen in the empty-list state too
// and gives way to a field error. The footer buttons carry no tooltip of their own.
const MergeTarget = ({ dataLoadOptions }: { dataLoadOptions: DataLoadOptions }) => {
  const { values } = useFormikContext<MergeFormValues>()
  return (
    <Field
      name="target"
      label="Merge into"
      help={values.target?._id ? undefined : targetRequired}
      showHelpAsInline
      component={ConversationSelect}
      dataLoadOptions={dataLoadOptions}
      noOptionsMessage={noOptionsMessage}
      isClearable={false}
      onChange
    />
  )
}

// Merge stays off until a target is picked, and again while the picked one is the one the server refused.
const MergeFooter = ({ values, isSubmitting, refusal }: FormikProps<MergeFormValues> & { refusal?: Refusal }) => (
  <SaveCancelButtons
    saveText="Merge"
    saveType="submit"
    saveIcon="code-merge"
    // No hover tooltip: the shared one opens beside the button and covers its sibling. The labels name the actions.
    saveTitle=""
    cancelType="reset"
    cancelTitle=""
    disabled={!values.target?._id || !!refusalFor(refusal, values.target)}
    isBusy={isSubmitting}
  />
)

export const MergeConversation = ({ sourceChat }: MergeConversationProps) => {
  const navigate = useNavigate()
  const { conversationItemRoute } = useConversationInboxContext()
  const { showSuccess } = useToast()
  const [merge] = useMergeChat()
  // The server's refusal, kept in the dialog beside the toast the form raises.
  const [refusal, setRefusal] = useState<Refusal>()
  const clearRefusal = () => setRefusal(undefined)
  // Only the conversations the server says the source can be merged into, searched by the typed text.
  const dataLoadOptions = {
    query: mergeableChatsQuery,
    resultFieldName: 'getMergeableChats',
    filter: ({ input }) => ({ ...buildConversationSearch(input), sourceChatId: sourceChat?._id }),
    options: conversationSelectOptions,
    // A merge changes the list, so a reopened dialog never reads a cached one.
    fetchPolicy: 'network-only',
  } as DataLoadOptions

  // A new footer type only when the refusal changes, so a click never lands on a footer that remounted under it.
  const footer = useCallback(
    (formik: FormikProps<MergeFormValues>) => <MergeFooter {...formik} refusal={refusal} />,
    [refusal]
  )

  // A refused merge rejects, so Form toasts it once and keeps the modal open; a resolved
  // submit closes the modal through FormModal's afterSubmit.
  const onSubmit = async ({ target }: MergeFormValues) => {
    clearRefusal()
    try {
      await merge({ sourceChatId: sourceChat?._id, targetChatId: target?._id })
    } catch (error) {
      setRefusal({ targetId: target?._id, message: (error as Error).message })
      throw error
    }
    showSuccess({ message: 'Conversation merged.' })
    navigate(conversationItemRoute.altPath({ _id: target?.externalId ?? target?._id }))
  }

  return (
    <FormModal<MergeFormValues>
      trigger={MergeTrigger}
      title="Merge this conversation into..."
      footer={footer}
      initialValues={{}}
      validate={validate}
      onSubmit={onSubmit}
      onReset={clearRefusal}
      onHide={clearRefusal}
    >
      <Stack gap={2}>
        <MergeRefusal refusal={refusal} />
        <MergeTarget dataLoadOptions={dataLoadOptions} />
      </Stack>
    </FormModal>
  )
}
